import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { chatbotApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { toCancelToken } from 'src/api/queries/queryClient';

export interface AdminFeedbackFilters {
  userId?: number;
  from?: Date;
  to?: Date;
  search?: string;
  rating?: string;
  reasons?: string[];
}

export interface AdminFeedbackParams extends AdminFeedbackFilters {
  page: number;
  pageSize: number;
}

const reasonsOrUndefined = (reasons?: string[]) =>
  reasons && reasons.length > 0 ? reasons : undefined;

/** @registry One page of rated F.I.R.E. replies for the admin Feedback tab, with rating/reason facet counts that ignore the rating and reason filters. */
export function useAdminFeedback(params: AdminFeedbackParams) {
  return useQuery({
    queryKey: queryKeys.chatAdmin.feedback(params),
    queryFn: ({ signal }) =>
      chatbotApiClient.listFeedback(
        params.page,
        params.pageSize,
        params.userId,
        params.from,
        params.to,
        params.search,
        params.rating,
        reasonsOrUndefined(params.reasons),
        toCancelToken(signal),
      ),
    placeholderData: keepPreviousData,
    meta: { errorContext: 'admin feedback' },
  });
}

/** @registry Downloads the filtered F.I.R.E. feedback votes as a CSV file; resolves the raw FileResponse for the caller to save. */
export function useExportAdminFeedback() {
  return useMutation({
    mutationFn: (filters: AdminFeedbackFilters) =>
      chatbotApiClient.exportFeedback(
        filters.userId,
        filters.from,
        filters.to,
        filters.search,
        filters.rating,
        reasonsOrUndefined(filters.reasons),
      ),
    meta: { errorContext: 'admin feedback export' },
  });
}
